import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Library, Loader2, Plus, Lock, BookOpen, CalendarRange, FileSpreadsheet, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API, CATEGORY_META, TEACHING_CATEGORIES, PLANNING_STEPS, WORKBOOK_ONLY_STEPS } from '@/components/resource-library/resourceLibraryApi';
import { ResourceFileRow } from '@/components/resource-library/ResourceFileRow';
import { ResourceUploadDialog } from '@/components/resource-library/ResourceUploadDialog';
import { ResourceViewerDialog } from '@/components/resource-library/ResourceViewerDialog';
import { PlannerCard } from '@/components/planning/PlannerCard';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

export default function ResourceLibraryPage() {
  const [pairs, setPairs] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selected, setSelected] = useState('');
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadCat, setUploadCat] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [planner, setPlanner] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetch(`${API}/api/resource-library/subjects`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        setPairs(d.subjects || []);
        setIsAdmin(!!d.is_admin);
        if (d.subjects?.length) setSelected(`${d.subjects[0].subject}|${d.subjects[0].year_level}`);
      })
      .finally(() => setLoading(false));
  }, []);

  const [subject, yearStr] = selected.split('|');
  const yearLevel = Number(yearStr);

  const reqId = React.useRef(0);
  const load = useCallback(() => {
    if (!subject) return;
    const id = ++reqId.current;
    fetch(`${API}/api/resource-library/staff?subject=${encodeURIComponent(subject)}&year_level=${yearLevel}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => { if (id === reqId.current) setResources(d.resources || []); });
  }, [subject, yearLevel]);

  useEffect(() => { setPlanner(null); load(); }, [load]);

  const remove = async (res) => {
    if (!window.confirm(`Delete "${res.title}"? Teachers and students will no longer see it.`)) return;
    const r = await fetch(`${API}/api/resource-library/${res.id}`, { method: 'DELETE', headers: authHeaders() });
    if (r.ok) { toast.success('Deleted'); load(); } else toast.error('Could not delete');
  };

  const byCat = (cat) => resources.filter((r) => r.category === cat);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8" data-testid="resource-library-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Library className="h-6 w-6 text-emerald-600" />Resource Library</h1>
          <p className="text-sm text-slate-500 mt-1">Click any document to open it here and step through the planning pack, textbook and activities in order — or download for offline use.</p>
        </div>
        <select value={selected} onChange={(e) => setSelected(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm bg-white min-w-[220px]" data-testid="resource-subject-select">
          {pairs.map((p) => (
            <option key={`${p.subject}|${p.year_level}`} value={`${p.subject}|${p.year_level}`}>
              Year {p.year_level} {p.subject}{p.has_files ? '' : ' (no files yet)'}
            </option>
          ))}
        </select>
      </div>

      {isAdmin && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-800" data-testid="resource-admin-banner">
          You are the library admin — use the <strong>Upload</strong> buttons to add files. Teachers and students only see View / Download.
        </div>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><BookOpen className="h-5 w-5 text-sky-600" />Teaching Resources</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {TEACHING_CATEGORIES.map((cat) => (
            <CategoryCard key={cat} cat={cat} files={byCat(cat)} token={token} isAdmin={isAdmin} onUpload={() => setUploadCat(cat)} onDelete={remove} onOpen={(r) => setViewId(r.id)} />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><CalendarRange className="h-5 w-5 text-amber-600" />Planning Pack <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200"><Lock className="h-3 w-3 mr-1" />Teachers only</Badge></h2>
        <p className="text-sm text-slate-500">Use the Interactive Planner to follow the year week by week, or open the PDF versions below.</p>
        <PlannerCard subject={subject} yearLevel={yearLevel} isAdmin={isAdmin} onSummary={setPlanner} />
        <div className="space-y-4">
          {PLANNING_STEPS.map((s) => (
            <CategoryCard key={s.key} cat={s.key} step={s.step} hint={s.hint} files={byCat(s.key)} token={token} isAdmin={isAdmin} onUpload={() => setUploadCat(s.key)} onDelete={remove} onOpen={(r) => setViewId(r.id)}
              workbook={planner?.workbook} onOpenPlanner={() => navigate(`/dashboard/planner?subject=${encodeURIComponent(subject)}&year=${yearLevel}&tab=${s.tab}`)} />
          ))}
          {WORKBOOK_ONLY_STEPS.filter((s) => planner?.workbook?.[s.flag]).map((s) => (
            <div key={s.key} className="rounded-xl border bg-slate-50/60 p-4 space-y-3" data-testid={`resource-category-${s.key}`}>
              <div className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center shrink-0">{s.step}</span>
                <div><Badge variant="outline" className={s.color}>{s.label}</Badge><p className="text-sm text-slate-600 mt-1">{s.hint}</p></div>
              </div>
              <button type="button" onClick={() => navigate(`/dashboard/planner?subject=${encodeURIComponent(subject)}&year=${yearLevel}&tab=${s.tab}`)} data-testid={`resource-planner-link-${s.key}`}
                className="w-full flex items-center gap-3 rounded-lg border border-l-4 border-l-emerald-500 bg-white px-4 py-3 shadow-sm hover:shadow-md hover:bg-emerald-50/40 transition-all text-left group">
                <div className="rounded-md bg-emerald-100 p-2 text-emerald-700"><FileSpreadsheet className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0"><p className="font-medium text-slate-900 group-hover:text-emerald-700">{s.label} — from the planning workbook</p><p className="text-xs text-slate-500 truncate">{planner.workbook.title} · click to open</p></div>
                <ArrowRight className="h-4 w-4 text-emerald-600" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {viewId && (
        <ResourceViewerDialog resources={resources} initialId={viewId} token={token} onClose={() => setViewId(null)} />
      )}

      {uploadCat && (
        <ResourceUploadDialog open onOpenChange={(v) => !v && setUploadCat(null)} subject={subject} yearLevel={yearLevel} category={uploadCat} onUploaded={load} />
      )}
    </div>
  );
}

export function CategoryCard({ cat, step, hint, files, token, isAdmin, onUpload, onDelete, onOpen, workbook, onOpenPlanner }) {
  const meta = CATEGORY_META[cat];
  return (
    <div className="rounded-xl border bg-slate-50/60 p-4 space-y-3" data-testid={`resource-category-${cat}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          {step && <span className="h-7 w-7 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center shrink-0">{step}</span>}
          <div>
            <Badge variant="outline" className={meta.color}>{meta.label}</Badge>
            {hint && <p className="text-sm text-slate-600 mt-1">{hint}</p>}
          </div>
        </div>
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={onUpload} data-testid={`resource-upload-btn-${cat}`}><Plus className="h-4 w-4 mr-1" />Upload</Button>
        )}
      </div>
      {workbook && (
        <button type="button" onClick={onOpenPlanner} data-testid={`resource-planner-link-${cat}`}
          className="w-full flex items-center gap-3 rounded-lg border border-l-4 border-l-emerald-500 bg-white px-4 py-3 shadow-sm hover:shadow-md hover:bg-emerald-50/40 transition-all text-left group">
          <div className="rounded-md bg-emerald-100 p-2 text-emerald-700"><FileSpreadsheet className="h-5 w-5" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 group-hover:text-emerald-700">{meta.label} — from the planning workbook</p>
            <p className="text-xs text-slate-500 truncate">{workbook.title} · interactive, click to open</p>
          </div>
          <ArrowRight className="h-4 w-4 text-emerald-600" />
        </button>
      )}
      {files.length === 0 ? (
        !workbook && <p className="text-sm text-slate-400 italic px-1" data-testid={`resource-empty-${cat}`}>Not uploaded yet.</p>
      ) : (
        <div className="space-y-2">
          {files.map((f) => <ResourceFileRow key={f.id} res={f} token={token} canDelete={isAdmin} onDelete={onDelete} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
}
